import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";

import { useTransferFlow } from "../../context/TransferFlowContext";
import { useTransfer } from "../../hooks/useMe";
import { PrivyCardOnrampButton } from "./PrivyCardOnrampButton";

const TERMINAL_STATUSES = ["completed", "failed", "cancelled"];
const DETAILS_REDIRECT_DELAY_MS = 3000;

export function TransactionConfirmed() {
  const navigate = useNavigate();
  const { transferId } = useTransferFlow();

  const { transferDetail, isLoadingDetail } = useTransfer(transferId, {
    pollDetail: true,
  });

  const status = transferDetail?.status;
  const fundingMethod = transferDetail?.funding_method;

  const isSettling =
    Boolean(status) && !TERMINAL_STATUSES.includes(status);

  const isFailed =
    status === "failed" || status === "cancelled";

  const isBankTransfer = fundingMethod === "bank";
  const isCardTransfer = fundingMethod === "card";

  useEffect(() => {
    // Bank-funded transfers require the customer to view the deposit
    // instructions on the transaction details page.
    if (
      !transferId ||
      isLoadingDetail ||
      !isBankTransfer ||
      !isSettling
    ) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      navigate(`/transaction/${transferId}`, {
        replace: true,
      });
    }, DETAILS_REDIRECT_DELAY_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    transferId,
    isLoadingDetail,
    isBankTransfer,
    isSettling,
    navigate,
  ]);

  // No transferId in context — someone landed here directly.
  if (!transferId) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6 text-center">
        <p className="text-sm text-[#0A4730]/50 mb-4">
          No transfer to show.
        </p>

        <button
          onClick={() => navigate("/home")}
          className="text-sm font-semibold text-[#0A4730] underline underline-offset-2"
        >
          Back to Home
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6 text-center">
      <div
        className={`w-24 h-24 rounded-full flex items-center justify-center mb-8 animate-in zoom-in duration-500 ${
          isFailed ? "bg-red-50" : "bg-green-50"
        }`}
      >
        <CheckCircle2
          size={48}
          className={
            isFailed ? "text-red-400" : "text-green-500"
          }
        />
      </div>

      <h1 className="text-3xl font-serif font-semibold text-[#0A4730] mb-3">
        {isLoadingDetail
          ? "Confirming…"
          : isFailed
          ? "Transfer failed"
          : isSettling
          ? "Transfer initiated"
          : "Transfer complete"}
      </h1>

      <p className="text-[#0A4730]/60 max-w-[280px] leading-relaxed mb-10">
        {isFailed
          ? transferDetail?.failure_reason ||
            "Something went wrong with this transfer."
          : isBankTransfer && isSettling
          ? "Your transfer has been initiated. Redirecting you to the deposit instructions…"
          : isSettling
          ? "Your transfer has been initiated. View details for any further action."
          : "Your transfer has reached the recipient."}
      </p>

      <div className="w-full max-w-sm space-y-3">
        {!isFailed && isCardTransfer && (
          <PrivyCardOnrampButton transferId={transferId} />
        )}

        <button
          onClick={() =>
            navigate(`/transaction/${transferId}`)
          }
          className="w-full bg-[#0A4730] text-white py-4 rounded-2xl font-medium flex items-center justify-center gap-2 active:scale-[0.98] transition"
        >
          View Details
          <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
}